import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IPricingAlignRequest, IPricingAlignResponse, IPricingGroupsResponse, IPricingMember, IPricingMembersRequest, PricingGroupBy } from "../models/pricing/IPricing";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export default function usePricing(groupBy: PricingGroupBy, membersRequest: IPricingMembersRequest | null, componentName = "usePricing") {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const groups = useQuery({
    queryKey: ["pricing", "groups", groupBy],
    queryFn: () => get<DefaultResponseWithData<IPricingGroupsResponse>>(`/pricing/groups?groupBy=${groupBy}`),
  });

  const members = useQuery({
    queryKey: ["pricing", "members", membersRequest],
    queryFn: () => {
      if (!membersRequest) throw new Error("No pricing group selected");
      return post<IPricingMembersRequest, DefaultResponseWithData<IPricingMember[]>>("/pricing/members", membersRequest);
    },
    enabled: !!membersRequest,
  });

  const align = useMutation(
    (body: IPricingAlignRequest) => post<IPricingAlignRequest, DefaultResponseWithData<IPricingAlignResponse>>("/pricing/align", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pricing"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
        queryClient.invalidateQueries(["listingStats"]);
      },
    }
  );

  const loadMembers = (request: IPricingMembersRequest) =>
    post<IPricingMembersRequest, DefaultResponseWithData<IPricingMember[]>>("/pricing/members", request);

  return {
    groups,
    members,
    align,
    loadMembers,
  };
}
